import { timeAgo } from './sourceStatus';
import {
  isBuildInFlight,
  type BuildSummary,
  type LastBuild,
} from '../../services/buildOrchestration.service';

/**
 * How the home page reads the last build. Kept apart from the component so the
 * rules can be tested without pulling the UI in.
 */

/** A build that stopped without landing data. */
const FAILED_STATUSES = ['ERROR', 'CANCELLED'];

export type BuildFreshnessKind =
  /** Nothing worth saying — still loading, or the request failed. */
  | 'unknown'
  /** A build is running right now. */
  | 'building'
  /** The newest build stopped without landing data. */
  | 'failed'
  /** Data landed and nothing has failed since. */
  | 'fresh'
  /** This branch has never built. */
  | 'never';

export type BuildFreshness = {
  kind: BuildFreshnessKind;
  /** What to say about it, or null when there's nothing to say. */
  note: string | null;
  /** Exact timestamp behind the note, for the hover title. */
  detail: string | null;
  /** Set only for 'failed': the orchestration whose logs explain the failure. */
  failedBuildId: string | null;
};

function parseDate(raw?: string | null): Date | null {
  if (!raw) return null;
  const date = new Date(raw);
  return isNaN(date.getTime()) ? null : date;
}

/** The moment a build came to rest, however it ended. */
function settledAt(build?: BuildSummary | null): Date | null {
  if (!build) return null;
  return parseDate(build.completed_at) || parseDate(build.queued_at);
}

/**
 * How fresh the data is, for the line under the greeting.
 *
 * A failure is only worth reporting when it's the *newest* thing that happened:
 * a build that failed before the last good one changed nothing, and the data is
 * as current as that good build left it.
 */
export function readBuildFreshness(lastBuild?: LastBuild | null): BuildFreshness {
  const nothing = { note: null, detail: null, failedBuildId: null };

  if (!lastBuild) return { kind: 'unknown', ...nothing };

  if (isBuildInFlight(lastBuild.latest_build)) {
    return { ...nothing, kind: 'building', note: 'updating data now' };
  }

  const lastSuccessAt = parseDate(lastBuild.last_successful_build_at);
  const latest = lastBuild.latest_build;
  const failedAt = FAILED_STATUSES.includes(latest?.status || '') ? settledAt(latest) : null;

  // The newest build failed after the last good one. The data is still there
  // and still usable — it just stopped moving at the earlier build.
  if (failedAt && (!lastSuccessAt || failedAt > lastSuccessAt)) {
    return {
      kind: 'failed',
      note: `Update failed ${timeAgo(failedAt)}`,
      detail: `Build failed ${failedAt.toLocaleString()}`,
      failedBuildId: latest?.id || null,
    };
  }

  if (lastSuccessAt) {
    return {
      ...nothing,
      kind: 'fresh',
      note: `data updated ${timeAgo(lastSuccessAt)}`,
    };
  }

  return { ...nothing, kind: 'never', note: 'no data builds yet' };
}

/** What to call the branch the timestamp came from, in the hover detail. */
export function environmentLabel(environment?: string | null): string {
  if (environment === 'PROD') return 'production';
  if (environment === 'DRAFT') return 'develop';
  return (environment || '').toLowerCase();
}

/** Exact timestamp behind the clause, shown on hover. */
export function describeLastBuildDetail(lastBuild?: LastBuild | null): string | undefined {
  if (!lastBuild) return undefined;

  const at = parseDate(lastBuild.last_successful_build_at);
  const where = environmentLabel(lastBuild.environment);
  if (!at) return where ? `No completed builds on ${where}` : undefined;

  return `Last ${where} build completed ${at.toLocaleString()}`;
}
